/* eslint-disable react/no-multi-comp, react/jsx-no-bind */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { AI_NOTES_SUMMARY_MODE_FINAL, fetchMeetingSummaryTimeline, getAiNotesServiceUrl } from '../../ai-notes/functions';
import { canRejoinHistory, filterHistoryItems, findRecordingForMeeting, formatClockHm, formatDurationClock, formatJoinShort, formatMeetingNo, formatMeetingTimeRange, groupHistoryByDate } from '../historyUtils';
import logger from '../logger';
import { PortalMessages as M } from '../messages';
function BackIcon() {
    return (React.createElement("svg", { fill: 'none', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("path", { d: 'M15 6l-6 6 6 6', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '2' })));
}
function MoreIcon() {
    return (React.createElement("svg", { fill: 'currentColor', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("circle", { cx: '6', cy: '12', r: '1.7' }),
        React.createElement("circle", { cx: '12', cy: '12', r: '1.7' }),
        React.createElement("circle", { cx: '18', cy: '12', r: '1.7' })));
}
function SearchIcon() {
    return (React.createElement("svg", { fill: 'none', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("circle", { cx: '11', cy: '11', r: '6.5', stroke: 'currentColor', strokeWidth: '1.8' }),
        React.createElement("path", { d: 'M16 16.5 20 20.5', stroke: 'currentColor', strokeLinecap: 'round', strokeWidth: '1.8' })));
}
function ChevronIcon() {
    return (React.createElement("svg", { fill: 'none', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("path", { d: 'M9 6l6 6-6 6', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '2' })));
}
function CopyIcon() {
    return (React.createElement("svg", { fill: 'none', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("rect", { height: '12', rx: '2', stroke: 'currentColor', strokeWidth: '1.7', width: '12', x: '8', y: '8' }),
        React.createElement("path", { d: 'M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2', stroke: 'currentColor', strokeWidth: '1.7' })));
}
function DocIcon() {
    return (React.createElement("svg", { fill: 'none', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("path", { d: 'M7 3.5h7l5 5V20a1.5 1.5 0 0 1-1.5 1.5H7A1.5 1.5 0 0 1 5.5 20V5A1.5 1.5 0 0 1 7 3.5Z', stroke: 'currentColor', strokeWidth: '1.6' }),
        React.createElement("path", { d: 'M14 3.5V9h5.5M8.5 13h7M8.5 16.5h5', stroke: 'currentColor', strokeLinecap: 'round', strokeWidth: '1.6' })));
}
function LastJoinIcon() {
    return (React.createElement("svg", { fill: 'none', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("path", { d: 'M12 7v5l3 2', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '1.8' }),
        React.createElement("path", { d: 'M4.8 12a7.2 7.2 0 1 0 2.1-5.1L4.8 9', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '1.8' })));
}
function DurationIcon() {
    return (React.createElement("svg", { fill: 'none', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("circle", { cx: '12', cy: '13', r: '7.2', stroke: 'currentColor', strokeWidth: '1.8' }),
        React.createElement("path", { d: 'M12 13V9.5M9 4.5h6', stroke: 'currentColor', strokeLinecap: 'round', strokeWidth: '1.8' })));
}
/**
 * 历史会议全屏页：列表按日期分组，点击进入会议详情（含 AI 纪要只读展示）.
 *
 * @param {IProps} props - Page props.
 * @returns {React.ReactElement}
 */
export default function HistoryPage({ busy, error, items, onBack, onDelete, onRefresh, onRejoin, onViewRecording, recordings }) {
    const [keyword, setKeyword] = useState('');
    const [selected, setSelected] = useState(null);
    const [copied, setCopied] = useState(false);
    const [moreMenu, setMoreMenu] = useState(null);
    const [timeline, setTimeline] = useState([]);
    const serviceUrl = useSelector((state) => getAiNotesServiceUrl(state));
    const groups = useMemo(() => groupHistoryByDate(filterHistoryItems(items, keyword)), [items, keyword]);
    const selectedRecording = selected
        ? findRecordingForMeeting(recordings, selected.meetingId)
        : undefined;
    useEffect(() => {
        if (selected && !items.some(item => item.historyId === selected.historyId)) {
            logger.info(`历史详情对应记录已不存在，返回列表 | historyId=${selected.historyId}`);
            setSelected(null);
        }
    }, [items, selected]);
    useEffect(() => {
        if (!selected) {
            setTimeline([]);
            return undefined;
        }
        const meetingKey = selected.meetingNo || String(selected.meetingId || '');
        if (!meetingKey) {
            logger.info(`历史会议缺少会议号，无法加载AI纪要 | meetingId=${selected.meetingId}`);
            setTimeline([]);
            return undefined;
        }
        let cancelled = false;
        logger.info(`[${new Date().toISOString()}] 加载会中AI纪要时间线`
            + ` | meetingId=${selected.meetingId} meetingNo=${meetingKey}`);
        fetchMeetingSummaryTimeline(serviceUrl, meetingKey)
            .then(timelineItems => {
            if (cancelled) {
                return;
            }
            setTimeline(timelineItems);
            logger.info(`会中AI纪要时间线已加载 | meetingNo=${meetingKey} count=${timelineItems.length}`);
        })
            .catch((e) => {
            if (cancelled) {
                return;
            }
            setTimeline([]);
            logger.warn(`加载会中AI纪要时间线失败 | meetingNo=${meetingKey} | ${e?.message || e}`);
        });
        return () => {
            cancelled = true;
        };
    }, [selected, serviceUrl]);
    const closeMore = useCallback(() => setMoreMenu(null), []);
    const copyMeetingNo = async (meetingNo) => {
        if (!meetingNo) {
            return;
        }
        try {
            await navigator.clipboard.writeText(meetingNo);
            setCopied(true);
            logger.info(`[${new Date().toISOString()}] 已复制会议号 | meetingNo=${meetingNo}`);
            window.setTimeout(() => setCopied(false), 1600);
        }
        catch (e) {
            logger.warn(`复制会议号失败 | ${e?.message || e}`);
        }
    };
    const openDetail = (item) => {
        logger.info(`[${new Date().toISOString()}] 打开历史会议详情`
            + ` | historyId=${item.historyId} meetingId=${item.meetingId}`);
        setSelected(item);
        setCopied(false);
        setMoreMenu(null);
        setTimeline([]);
    };
    const backToList = () => {
        logger.info(`[${new Date().toISOString()}] 历史会议详情返回列表`);
        setSelected(null);
        setCopied(false);
        setMoreMenu(null);
        setTimeline([]);
    };
    const organizerInitial = (selected?.organizerName || '?').slice(0, 1);
    return (React.createElement("div", { className: 'portal-history-page', onClick: closeMore },
        React.createElement("header", { className: 'portal-history-page-header' },
            React.createElement("button", { "aria-label": M.HISTORY_BACK, className: 'portal-history-page-icon-btn', onClick: selected ? backToList : onBack, type: 'button' },
                React.createElement(BackIcon, null)),
            React.createElement("h2", null, selected ? '' : M.HISTORY),
            React.createElement("div", { className: 'portal-history-more-wrap', onClick: event => event.stopPropagation() },
                React.createElement("button", { "aria-label": M.HISTORY_MORE, className: 'portal-history-page-icon-btn', onClick: () => setMoreMenu(prev => (prev ? null : (selected ? 'detail' : 'list'))), type: 'button' },
                    React.createElement(MoreIcon, null)),
                moreMenu === 'list' ? (React.createElement("div", { className: 'portal-history-more-menu', role: 'menu' },
                    React.createElement("button", { disabled: busy, onClick: () => {
                            closeMore();
                            logger.info(`[${new Date().toISOString()}] 刷新历史会议列表`);
                            onRefresh();
                        }, role: 'menuitem', type: 'button' }, M.HISTORY_REFRESH))) : null,
                moreMenu === 'detail' && selected ? (React.createElement("div", { className: 'portal-history-more-menu', role: 'menu' },
                    canRejoinHistory(selected) ? (React.createElement("button", { disabled: busy, onClick: () => {
                            closeMore();
                            onRejoin(selected);
                        }, role: 'menuitem', type: 'button' }, M.HISTORY_REJOIN)) : null,
                    selectedRecording ? (React.createElement("button", { disabled: busy, onClick: () => {
                            closeMore();
                            onViewRecording(selected);
                        }, role: 'menuitem', type: 'button' }, M.HISTORY_VIEW_RECORDING)) : null,
                    selected.organizer && selected.meetingId ? (React.createElement("button", { className: 'danger', disabled: busy, onClick: () => {
                            closeMore();
                            onDelete(selected.meetingId);
                        }, role: 'menuitem', type: 'button' }, M.DELETE_MEETING)) : null,
                    !canRejoinHistory(selected) && !selectedRecording
                        && !(selected.organizer && selected.meetingId) ? (React.createElement("button", { disabled: true, role: 'menuitem', type: 'button' }, M.HISTORY_ENDED_HINT)) : null)) : null)),
        selected ? (React.createElement("div", { className: 'portal-history-detail-page' },
            React.createElement("div", { className: 'portal-history-detail-scroll' },
                React.createElement("div", { className: 'portal-history-detail-title' }, selected.title || selected.meetingNo || M.NO_TITLE),
                React.createElement("div", { className: 'portal-history-detail-time' }, formatMeetingTimeRange(selected)),
                React.createElement("div", { className: 'portal-history-detail-id' },
                    React.createElement("span", null,
                        M.HISTORY_MEETING_NO,
                        ": ",
                        formatMeetingNo(selected.meetingNo)
                            || selected.meetingNo
                            || '--'),
                    selected.meetingNo ? (React.createElement("button", { "aria-label": copied ? M.HISTORY_COPIED : M.HISTORY_COPY, className: 'portal-history-copy', onClick: () => copyMeetingNo(selected.meetingNo), type: 'button' },
                        React.createElement(CopyIcon, null))) : null,
                    copied ? (React.createElement("span", { className: 'portal-history-copied' }, M.HISTORY_COPIED)) : null),
                React.createElement("div", { className: 'portal-history-organizer-pill' },
                    React.createElement("span", { className: 'label' }, M.HISTORY_ORGANIZER),
                    React.createElement("span", { className: 'avatar' }, organizerInitial),
                    React.createElement("span", { className: 'name' }, selected.organizerName || '--')),
                React.createElement("div", { className: 'portal-history-stats' },
                    React.createElement("div", { className: 'portal-history-stat' },
                        React.createElement("span", { className: 'icon' },
                            React.createElement(LastJoinIcon, null)),
                        React.createElement("div", { className: 'value' }, formatJoinShort(selected.joinedAt)),
                        React.createElement("div", { className: 'label' }, M.HISTORY_LAST_JOIN)),
                    React.createElement("div", { className: 'portal-history-stat' },
                        React.createElement("span", { className: 'icon' },
                            React.createElement(DurationIcon, null)),
                        React.createElement("div", { className: 'value' }, formatDurationClock(selected.durationSeconds)),
                        React.createElement("div", { className: 'label' }, M.HISTORY_DURATION))),
                error ? React.createElement("div", { className: 'portal-error inline' }, error) : null,
                React.createElement("section", { className: 'portal-history-ai' },
                    React.createElement("div", { className: 'portal-history-ai-head' },
                        React.createElement("span", { className: 'title' }, M.HISTORY_AI_NOTES),
                        React.createElement("span", { className: 'tag' }, M.HISTORY_AI_DEVICE_ONLY)),
                    timeline.length === 0 ? (React.createElement("div", { className: 'portal-history-ai-empty' }, M.HISTORY_NO_NOTES)) : (timeline.map((entry, index) => {
                        const isFinal = entry.summaryMode === AI_NOTES_SUMMARY_MODE_FINAL;
                        const generatedAt = entry.updatedAt
                            ? new Date(entry.updatedAt).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit'
                            })
                            : '';
                        return (React.createElement("article", { className: 'portal-history-ai-card', key: `${entry.updatedAt || 'seg'}-${index}` },
                            React.createElement("div", { className: 'card-title' }, isFinal ? M.HISTORY_AI_FINAL : M.HISTORY_AI_OVERVIEW),
                            entry.overview ? (React.createElement("div", { className: 'card-overview' }, entry.overview)) : null,
                            entry.decisions?.length ? (React.createElement(React.Fragment, null,
                                React.createElement("div", { className: 'card-section' }, M.HISTORY_AI_DECISIONS),
                                React.createElement("ul", { className: 'card-list' }, entry.decisions.map((item, i) => (React.createElement("li", { key: `d-${index}-${i}` }, item)))))) : null,
                            entry.actionItems?.length ? (React.createElement(React.Fragment, null,
                                React.createElement("div", { className: 'card-section' }, M.HISTORY_AI_ACTIONS),
                                React.createElement("ul", { className: 'card-list' }, entry.actionItems.map((item, i) => (React.createElement("li", { key: `a-${index}-${i}` }, [item.owner, item.task, item.due]
                                    .filter(Boolean)
                                    .join(' · '))))))) : null,
                            React.createElement("div", { className: 'card-meta' }, generatedAt
                                ? `${generatedAt} · ${M.HISTORY_AI_GENERATED}`
                                : M.HISTORY_AI_GENERATED)));
                    })))),
            React.createElement("div", { className: 'portal-history-detail-footer' }, canRejoinHistory(selected) ? (React.createElement("button", { disabled: busy, onClick: () => onRejoin(selected), type: 'button' }, M.HISTORY_REJOIN)) : (React.createElement("span", null, M.HISTORY_ENDED_HINT))))) : (React.createElement("div", { className: 'portal-history-list-page' },
            React.createElement("div", { className: 'portal-history-search-wrap' },
                React.createElement("span", { className: 'portal-history-search-icon' },
                    React.createElement(SearchIcon, null)),
                React.createElement("input", { className: 'portal-history-search', onChange: e => setKeyword(e.target.value), placeholder: M.HISTORY_SEARCH_PLACEHOLDER, value: keyword })),
            error ? React.createElement("div", { className: 'portal-error inline' }, error) : null,
            groups.length === 0 ? (React.createElement("div", { className: 'portal-empty portal-history-list-empty' }, M.NO_HISTORY)) : (React.createElement("div", { className: 'portal-history-groups' }, groups.map(group => (React.createElement("div", { className: 'portal-history-group', key: group.key },
                React.createElement("div", { className: 'portal-history-group-label' },
                    React.createElement("span", null, group.dateLabel),
                    React.createElement("span", null, group.yearLabel)),
                React.createElement("ul", { className: 'portal-list portal-history-list' }, group.items.map(item => {
                    const hasDoc = Boolean(findRecordingForMeeting(recordings, item.meetingId));
                    return (React.createElement("li", { className: 'portal-history-item', key: item.historyId, onClick: () => openDetail(item) },
                        React.createElement("div", { className: 'portal-history-item-main' },
                            item.meetingNo ? (React.createElement("div", { className: 'meeting-no' }, formatMeetingNo(item.meetingNo))) : null,
                            React.createElement("div", { className: 'title' },
                                item.title || item.meetingNo || M.NO_TITLE,
                                hasDoc ? (React.createElement("span", { className: 'doc-icon' },
                                    React.createElement(DocIcon, null))) : null),
                            React.createElement("div", { className: 'meta' },
                                `${M.HISTORY_TIME}：${formatClockHm(item.joinedAt) || '--'}`,
                                item.organizerName
                                    ? `  ${M.HISTORY_ORGANIZER}：${item.organizerName}`
                                    : '')),
                        React.createElement("span", { className: 'portal-history-chevron' },
                            React.createElement(ChevronIcon, null))));
                })))))))))));
}
